/**
 * Sajilni Event Tier — PostHog event catalog (TypeScript)
 * Canonical capture contract. Re-exported from `@/lib/analytics/catalog`.
 *
 * `$screen` + `$screen_name` power PostHog screen insights.
 * `participant_type` uses the values below — not Prisma singular enums.
 */

export enum AnalyticsPlatform {
  Web = 'web',
  Ios = 'ios',
  Android = 'android',
}

/** Actor participant type in the current event context */
export enum ParticipantType {
  Exhibitors = 'EXHIBITORS',
  Speakers = 'SPEAKERS',
  Visitors = 'VISITORS',
  Sponsors = 'SPONSORS',
  Media = 'MEDIA',
  Support = 'SUPPORT',
  Other = 'OTHER',
}

export enum AnalyticsEnvironment {
  Production = 'production',
  Staging = 'staging',
  Development = 'development',
}

export enum CompanyType {
  Exhibitor = 'exhibitor',
  Sponsor = 'sponsor',
}

export enum NotificationChannel {
  Push = 'push',
}

export enum CompanyFileAction {
  View = 'view',
  Download = 'download',
}

export enum AnalyticsEntityType {
  File = 'FILE',
  Video = 'VIDEO',
  Product = 'PRODUCT',
}

/**
 * Prefer PostHog default lifecycle/navigation events where they exist.
 * Confirm mobile open raw name in project ($app_opened vs Application Opened).
 */
export enum PostHogEventName {
  AppOpened = '$app_opened',
  Screen = '$screen',
  Pageview = '$pageview',
  AgendaSessionViewed = 'agenda_session_viewed',
  CompanyProfileViewed = 'company_profile_viewed',
  CompanyContactClicked = 'company_contact_clicked',
  ProfileViewed = 'profile_viewed',
  NotificationOpened = 'notification_opened',
  RegistrationStarted = 'registration_started',
  RegistrationFormCompleted = 'registration_form_completed',
  PaymentInitiated = 'payment_initiated',
  RegistrationCompleted = 'registration_completed',
  CompanyFileViewed = 'company_file_viewed',
  CompanyVideoViewed = 'company_video_viewed',
  CompanyProductViewed = 'company_product_viewed',
}

/**
 * Super-properties — register so they attach to every event.
 * white_label_id + event_id stay required for tenant isolation.
 */
export interface PostHogSuperProperties {
  platform: AnalyticsPlatform;
  app_source: string;
  app_version: string;
  white_label_id: string;
  event_id: string;
  event_slug: string;
  environment: AnalyticsEnvironment;
  locale: string;
}

/**
 * Optional on events for guest mode. Required when logged in for identify.
 */
export interface PostHogActorProperties {
  user_id?: string;
  profile_id?: string;
  participant_type?: ParticipantType;
}

export type PostHogFixedProperties = PostHogSuperProperties & PostHogActorProperties;

/** Person properties set via identify(user_id, { ... }) — not on every event */
export interface PostHogPersonProperties {
  user_id: string;
}

export interface AppOpenedProperties extends PostHogFixedProperties {}

export interface ScreenProperties extends PostHogFixedProperties {
  $screen_name: string;
}

export interface PageviewProperties extends PostHogFixedProperties {}

export interface AgendaSessionViewedProperties extends PostHogFixedProperties {
  session_id: string;
  session_name?: string;
  track?: string;
}

export interface CompanyProfileViewedProperties extends PostHogFixedProperties {
  company_id: string;
  company_type: CompanyType;
  company_name?: string;
}

export interface CompanyContactClickedProperties extends PostHogFixedProperties {
  company_id: string;
  company_type: CompanyType;
  /** Optional free-form string — not a strict enum */
  contact_method?: string;
}

export interface ProfileViewedProperties extends PostHogFixedProperties {
  viewed_user_id: string;
  viewed_profile_id?: string;
  viewed_participant_type?: ParticipantType | string;
}

export interface NotificationOpenedProperties extends PostHogFixedProperties {
  notification_id: string;
  notification_type?: string;
  channel: NotificationChannel;
  campaign_id?: string;
}

export interface RegistrationStartedProperties extends PostHogFixedProperties {
  ticket_id?: string;
  form_id?: string;
}

export interface RegistrationFormCompletedProperties extends PostHogFixedProperties {
  ticket_id?: string;
  form_id?: string;
}

export interface PaymentInitiatedProperties extends PostHogFixedProperties {
  order_id?: string;
  amount?: number;
  currency?: string;
}

export interface RegistrationCompletedProperties extends PostHogFixedProperties {
  order_id?: string;
  ticket_id?: string;
}

export interface CompanyFileViewedProperties extends PostHogFixedProperties {
  entity_id: string;
  entity_type: AnalyticsEntityType.File;
  company_id: string;
  company_type: CompanyType;
  file_name?: string;
  action?: CompanyFileAction;
}

export interface CompanyVideoViewedProperties extends PostHogFixedProperties {
  entity_id: string;
  entity_type: AnalyticsEntityType.Video;
  company_id: string;
  company_type: CompanyType;
  video_name?: string;
  watch_percent?: number;
}

export interface CompanyProductViewedProperties extends PostHogFixedProperties {
  entity_id: string;
  entity_type: AnalyticsEntityType.Product;
  company_id: string;
  company_type: CompanyType;
  product_name?: string;
}

export type PostHogEventPropertiesMap = {
  [PostHogEventName.AppOpened]: AppOpenedProperties;
  [PostHogEventName.Screen]: ScreenProperties;
  [PostHogEventName.Pageview]: PageviewProperties;
  [PostHogEventName.AgendaSessionViewed]: AgendaSessionViewedProperties;
  [PostHogEventName.CompanyProfileViewed]: CompanyProfileViewedProperties;
  [PostHogEventName.CompanyContactClicked]: CompanyContactClickedProperties;
  [PostHogEventName.ProfileViewed]: ProfileViewedProperties;
  [PostHogEventName.NotificationOpened]: NotificationOpenedProperties;
  [PostHogEventName.RegistrationStarted]: RegistrationStartedProperties;
  [PostHogEventName.RegistrationFormCompleted]: RegistrationFormCompletedProperties;
  [PostHogEventName.PaymentInitiated]: PaymentInitiatedProperties;
  [PostHogEventName.RegistrationCompleted]: RegistrationCompletedProperties;
  [PostHogEventName.CompanyFileViewed]: CompanyFileViewedProperties;
  [PostHogEventName.CompanyVideoViewed]: CompanyVideoViewedProperties;
  [PostHogEventName.CompanyProductViewed]: CompanyProductViewedProperties;
};

export type PostHogCapturePayload<E extends PostHogEventName = PostHogEventName> = {
  event: E;
  properties: PostHogEventPropertiesMap[E];
};

export type CapturePostHogEvent = <E extends PostHogEventName>(
  event: E,
  properties: PostHogEventPropertiesMap[E]
) => void;
